export const colors = {
  blue: "#2f7cf6",
  green: "#18b884",
  orange: "#ff9f1a",
  purple: "#6c63ff",
  red: "#e64a55",
  cyan: "#12b5cb",
  gray: "#8a97aa",
};

const compactNumber = (value) => {
  const number = Number(value || 0);
  if (Math.abs(number) >= 10000) {
    return `${(number / 1000).toFixed(number >= 100000 ? 0 : 1)}k`;
  }
  return String(number);
};

const tooltip = (trigger = "axis") => ({
  trigger,
  backgroundColor: "rgba(24, 38, 61, 0.94)",
  borderWidth: 0,
  padding: [8, 10],
  textStyle: { color: "#fff", fontSize: 12 },
  axisPointer:
    trigger === "axis"
      ? {
          type: "line",
          lineStyle: { color: "rgba(96, 112, 137, 0.35)" },
        }
      : undefined,
});

const categoryAxis = (labels, boundaryGap = false) => ({
  type: "category",
  boundaryGap,
  data: labels,
  axisTick: { show: false },
  axisLine: { lineStyle: { color: "#dfe6ef" } },
  axisLabel: { color: "#8a97aa", fontSize: 10 },
});

const valueAxis = (showSplit = true) => ({
  type: "value",
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: {
    color: "#8a97aa",
    fontSize: 10,
    formatter: compactNumber,
  },
  splitLine: showSplit
    ? { lineStyle: { color: "#edf1f7", type: "dashed" } }
    : { show: false },
});

const legend = (names) => ({
  data: names,
  bottom: 0,
  itemWidth: 14,
  itemHeight: 6,
  textStyle: { color: "#758399", fontSize: 10 },
});

export function lineTrendOption(trend, definitions, dualAxis = false) {
  const rows = trend || [];
  return {
    animationDuration: 350,
    tooltip: tooltip(),
    legend: legend(definitions.map((item) => item.name)),
    grid: {
      top: 12,
      left: 8,
      right: 8,
      bottom: 34,
      containLabel: true,
    },
    xAxis: categoryAxis(rows.map((item) => item.label)),
    yAxis: dualAxis ? [valueAxis(true), valueAxis(false)] : valueAxis(true),
    series: definitions.map((definition) => ({
      name: definition.name,
      type: "line",
      yAxisIndex: definition.yAxisIndex || 0,
      data: rows.map((item) => item[definition.key]),
      connectNulls: false,
      smooth: true,
      showSymbol: true,
      symbol: "circle",
      symbolSize: 5,
      lineStyle: { width: 2, color: definition.color },
      itemStyle: { color: definition.color },
      emphasis: { focus: "series" },
    })),
  };
}

export function leadBarOption(trend) {
  const rows = trend || [];
  return {
    animationDuration: 350,
    tooltip: tooltip(),
    grid: {
      top: 12,
      left: 8,
      right: 8,
      bottom: 22,
      containLabel: true,
    },
    xAxis: categoryAxis(
      rows.map((item) => item.label),
      true
    ),
    yAxis: {
      ...valueAxis(true),
      minInterval: 1,
    },
    series: [
      {
        name: "询盘量",
        type: "bar",
        barMaxWidth: 28,
        data: rows.map((item) => item.count),
        itemStyle: {
          color: colors.green,
          borderRadius: [4, 4, 0, 0],
        },
      },
    ],
  };
}

export function countryPieOption(rows, isMobile = window.innerWidth <= 768) {
  const items = (rows || []).map((item) => ({
    name: item.country,
    value: item.sessions,
    percentage: item.percentage,
  }));
  const itemMap = items.reduce((map, item) => {
    map[item.name] = item;
    return map;
  }, {});

  return {
    animationDuration: 350,
    tooltip: {
      ...tooltip("item"),
      formatter: (params) =>
        `${params.name}<br/>会话数：${Number(params.value || 0).toLocaleString(
          "zh-CN"
        )}（${params.data.percentage}%）`,
    },
    legend: {
      show: !isMobile,
      orient: "vertical",
      right: 10,
      top: "center",
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 9,
      data: items.map((item) => item.name),
      textStyle: {
        color: "#526178",
        fontSize: 12,
      },
      formatter: (name) => {
        const item = itemMap[name];
        return item ? `${name}  (${item.percentage}%)` : name;
      },
    },
    color: [
      colors.blue,
      colors.green,
      colors.orange,
      colors.purple,
      colors.cyan,
      "#ff6b6b",
      "#5c8df6",
      "#9b8cff",
      "#48c9b0",
      "#8a97aa",
      "#b7c0cc",
    ],
    series: [
      {
        name: "国家来源",
        type: "pie",
        radius: isMobile ? ["30%", "55%"] : ["40%", "60%"],
        center: isMobile ? ["50%", "50%"] : ["35%", "50%"],
        avoidLabelOverlap: true,
        minShowLabelAngle: 0,
        legendHoverLink: true,
        itemStyle: {
          borderColor: "#fff",
          borderWidth: 2,
          borderRadius: 6,
        },
        label: {
          show: true,
          position: "outside",
          formatter: (params) =>
            isMobile
              ? `${params.data.percentage}%`
              : `${params.name}\n(${params.data.percentage}%)`,
          color: "#526178",
          fontSize: isMobile ? 11 : 10,
          lineHeight: isMobile ? 14 : 15,
        },
        labelLine: {
          show: true,
          length: isMobile ? 10 : 15,
          length2: isMobile ? 10 : 20,
          lineStyle: { color: "#aeb9c8", width: 1 },
        },
        labelLayout: {
          hideOverlap: true,
          moveOverlap: "shiftY",
        },
        emphasis: {
          scale: true,
          scaleSize: 10,
          itemStyle: {
            shadowBlur: 14,
            shadowColor: "rgba(24, 38, 61, 0.26)",
          },
          label: {
            show: true,
            fontSize: 13,
            fontWeight: 700,
          },
        },
        data: items,
      },
    ],
  };
}

export function channelBarOption(rows) {
  const items = rows || [];
  return {
    tooltip: {
      ...tooltip(),
      axisPointer: { type: "shadow" },
    },
    grid: {
      left: 6,
      right: 32,
      top: 6,
      bottom: 6,
      containLabel: true,
    },
    xAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: { show: false },
    },
    yAxis: {
      type: "category",
      inverse: true,
      data: items.map((item) => item.display_name),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: "#607089",
        fontSize: 11,
        width: 150,
        overflow: "truncate",
      },
    },
    series: [
      {
        name: "会话数",
        type: "bar",
        data: items.map((item) => item.sessions),
        barWidth: 14,
        label: {
          show: true,
          position: "right",
          color: "#607089",
          fontSize: 10,
        },
        itemStyle: {
          color: colors.blue,
          borderRadius: [0, 4, 4, 0],
        },
      },
    ],
  };
}

export function operationTrendOption(trend, color) {
  const rows = trend || [];
  return {
    animationDuration: 350,
    tooltip: tooltip(),
    legend: legend(["月更新", "累计"]),
    grid: {
      top: 12,
      left: 8,
      right: 8,
      bottom: 34,
      containLabel: true,
    },
    xAxis: categoryAxis(
      rows.map((item) => item.label),
      true
    ),
    yAxis: {
      ...valueAxis(true),
      minInterval: 1,
    },
    series: [
      {
        name: "月更新",
        type: "bar",
        barMaxWidth: 24,
        data: rows.map((item) => item.monthly_count),
        itemStyle: {
          color,
          borderRadius: [4, 4, 0, 0],
        },
      },
      {
        name: "累计",
        type: "line",
        smooth: true,
        data: rows.map((item) => item.cumulative_count),
        lineStyle: { width: 2, color: colors.blue },
        itemStyle: { color: colors.blue },
      },
    ],
  };
}
